import type { LockedRecord } from "./lockOverride";

/**
 * The line between the axios interceptor and the lock override modal (J/665). The
 * interceptor is not a React component, so it asks here and waits. The modal
 * (LockOverrideModal) shows the first waiting request. Requests that arrive while a
 * modal is open wait their turn, so two saves never open two modals.
 */

export interface LockOverrideRequest {
  records: LockedRecord[];
  previousReason: string;
  resolve: (reason: string | null) => void;
}

let queue: LockOverrideRequest[] = [];
let mounted = 0;
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((listener) => listener());

export const subscribeToLockOverrides = (listener: () => void): (() => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

/** The request the modal shows now, or null. The same object until it is answered. */
export const currentLockOverrideRequest = (): LockOverrideRequest | null => queue[0] ?? null;

/**
 * The modal says when it is on the page. With none, a request is answered "cancel" at once,
 * so a screen without the modal gets its original error instead of waiting for ever.
 */
export const registerLockOverrideModal = (): (() => void) => {
  mounted += 1;

  return () => {
    mounted -= 1;

    if (mounted === 0) {
      const waiting = queue;
      queue = [];
      emit();
      waiting.forEach((request) => request.resolve(null));
    }
  };
};

/** Resolves with the reason the person gave, or null when they cancel. */
export const requestLockOverrideReason = (records: LockedRecord[], previousReason = ""): Promise<string | null> =>
  new Promise((resolve) => {
    if (mounted === 0) {
      resolve(null);

      return;
    }

    queue = [...queue, { records, previousReason, resolve }];
    emit();
  });

export const answerLockOverride = (reason: string | null): void => {
  const [current, ...rest] = queue;

  if (!current) {
    return;
  }

  queue = rest;
  emit();
  current.resolve(reason);
};
